import React, { useEffect, useRef, useState } from 'react';
import { LoadScript } from '@react-google-maps/api';
import { Input } from '@design-system/components/Inputs/Input/Input';
import './AutocompleteInput.scss';

declare global {
    interface Window {
        google: typeof google;
    }
}

export interface StructuredAddress {
    address: string;
    city: string;
    country: string;
    postal_code: string;
}

export type AutocompleteInputProps = {
    children: React.ReactNode;
    apiKey?: string;
    onPlaceSelect?: (place: StructuredAddress) => void;
};

export const AutocompleteInput: React.FC<AutocompleteInputProps> = ({
    children,
    apiKey = 'AIzaSyDhoi2UM63hVNYu_EY83nc-EulpJRO2eBY',
    onPlaceSelect
}) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

    const extractAddressComponents = (place: google.maps.places.PlaceResult): StructuredAddress => {
        const addressComponents = place.address_components || [];
        const structuredAddress: StructuredAddress = {
            address: '',
            city: '',
            country: '',
            postal_code: ''
        };

        // Extraction de l'adresse (rue, numéro, etc.)
        const streetNumber = addressComponents.find((component) => component.types.includes('street_number'))?.long_name || '';

        const route = addressComponents.find((component) => component.types.includes('route'))?.long_name || '';

        structuredAddress.address = `${streetNumber} ${route}`.trim();

        // Extraction des autres composants
        addressComponents.forEach((component) => {
            const types = component.types;
            if (types.includes('locality') || types.includes('sublocality')) {
                structuredAddress.city = component.long_name;
            } else if (types.includes('country')) {
                structuredAddress.country = component.long_name;
            } else if (types.includes('postal_code')) {
                structuredAddress.postal_code = component.long_name;
            }
        });

        return structuredAddress;
    };

    const initAutocomplete = () => {
        if (!inputRef.current || !window.google || autocompleteRef.current) return;

        // Coordonnées de Toulon
        const toulonBounds = new window.google.maps.LatLngBounds(
            new window.google.maps.LatLng(43.1064, 5.93),
            new window.google.maps.LatLng(43.1364, 5.97)
        );

        const toulonCenter = new window.google.maps.LatLng(43.1214, 5.95);

        const options = {
            componentRestrictions: { country: 'fr' },
            fields: ['address_components', 'formatted_address', 'geometry'],
            types: ['address'],
            bounds: toulonBounds,
            location: toulonCenter,
            strictBounds: false
        };

        try {
            const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, options);

            // Gérer le scroll dans rendered-page
            const renderedPage = document.querySelector('.rendered-page');
            if (renderedPage) {
                renderedPage.addEventListener('scroll', () => {
                    if (inputRef.current) {
                        inputRef.current.blur(); // Ferme l'autocomplete en retirant le focus
                    }
                });
            }

            autocomplete.addListener('place_changed', () => {
                const place = autocomplete.getPlace();
                if (onPlaceSelect) {
                    const structuredAddress = extractAddressComponents(place);
                    onPlaceSelect(structuredAddress);
                }
            });

            autocompleteRef.current = autocomplete;
        } catch (error) {
            console.error("Erreur lors de l'initialisation de l'autocomplétion:", error);
        }
    };

    useEffect(() => {
        if (isLoaded) {
            initAutocomplete();
        }
    }, [isLoaded]);

    const handleLoad = () => {
        setIsLoaded(true);
    };

    const handleError = (error: Error) => {
        console.error('Erreur de chargement de Google Maps:', error);
    };

    const renderChildren = () => {
        return React.Children.map(children, (child) => {
            if (React.isValidElement(child) && child.type === Input) {
                return React.cloneElement(child as React.ReactElement<typeof Input>, {
                    ref: inputRef,
                    ...child.props
                });
            }
            return child;
        });
    };

    return (
        <LoadScript
            googleMapsApiKey={apiKey}
            libraries={['places']}
            onLoad={handleLoad}
            onError={handleError}
            loadingElement={<div className="autocomplete-input">{renderChildren()}</div>}
        >
            <div className="autocomplete-input">{renderChildren()}</div>
        </LoadScript>
    );
};
